import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";

const PrivacyPolicy = () => {
  const lastUpdated = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-[#E2E8F0] z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#8B1B3A] rounded flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-xl font-semibold text-[#0F172A]">KlinikTrack</span>
              <span className="hidden sm:inline text-xs text-[#94A3B8] ml-2">by 0g Holding SA</span>
            </div>
          </Link>
          <Link 
            to="/" 
            className="text-sm text-[#8B1B3A] hover:underline"
          >
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-2">
            Privacy Policy (GDPR – DACH)
          </h1>
          <p className="text-sm text-[#64748B] mb-8">
            Last updated: {lastUpdated}
          </p>

          <div className="prose prose-slate max-w-none">
            <p className="text-[#475569] leading-relaxed mb-6">
              This Privacy Policy explains how 0g Holding SA ("we", "us") processes personal data in connection with KlinikTrack (website and demo platform). KlinikTrack is designed for hospital operations. KlinikTrack does not require patient data and is not intended to track patients or staff.
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">1. Controller</h2>
            <p className="text-[#475569] leading-relaxed mb-2">
              Controller: 0g Holding SA<br />
              Address: Rue Cavour 1, 1203 Geneva, Switzerland<br />
              Email: contact@0gholding.ch
            </p>
            <p className="text-[#475569] leading-relaxed mb-6">
              For visitors in the EU/EEA, this policy is intended to comply with the General Data Protection Regulation (GDPR). For Switzerland, processing is aligned with the Swiss Federal Act on Data Protection (FADP).
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">2. Scope</h2>
            <p className="text-[#475569] leading-relaxed mb-2">This policy applies to:</p>
            <ul className="list-disc pl-6 text-[#475569] mb-6 space-y-1">
              <li>Website visits (landing pages, contact forms, demo access requests)</li>
              <li>Demo platform access (authentication and usage)</li>
              <li>Business communications related to KlinikTrack</li>
            </ul>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">3. Personal data we collect</h2>
            <p className="text-[#475569] leading-relaxed mb-4">Depending on interaction, we may collect:</p>
            
            <h3 className="text-lg font-medium text-[#0F172A] mt-4 mb-2">A) Demo access request data</h3>
            <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-1">
              <li>Name, organization, role, department</li>
              <li>Email, optional phone number</li>
              <li>Country, facility type, hospital size (beds range)</li>
              <li>Assets to be tracked, target timeframe, comments</li>
              <li>How you heard about KlinikTrack</li>
              <li>GDPR consent status and timestamp</li>
            </ul>

            <h3 className="text-lg font-medium text-[#0F172A] mt-4 mb-2">B) Demo platform data</h3>
            <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-1">
              <li>Authentication data (email, login metadata)</li>
              <li>Basic usage logs for security and service improvement</li>
            </ul>

            <h3 className="text-lg font-medium text-[#0F172A] mt-4 mb-2">C) Technical data</h3>
            <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-1">
              <li>IP address, browser and device information</li>
              <li>Approximate location (country/city level)</li>
              <li>Website analytics data (if enabled)</li>
            </ul>
            <p className="text-[#475569] leading-relaxed mb-6">
              We do not intentionally collect health data, patient data, or staff tracking data.
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">4. Purposes and legal bases</h2>
            <p className="text-[#475569] leading-relaxed mb-4">Personal data is processed for the following purposes:</p>
            <ul className="list-none pl-0 text-[#475569] mb-6 space-y-3">
              <li>
                <strong>Providing demo access and responding to requests</strong><br />
                Legal basis: GDPR Art. 6(1)(b) and/or Art. 6(1)(f)
              </li>
              <li>
                <strong>Business communication and follow-up</strong><br />
                Legal basis: Art. 6(1)(f)
              </li>
              <li>
                <strong>Security, fraud prevention, and service integrity</strong><br />
                Legal basis: Art. 6(1)(f)
              </li>
              <li>
                <strong>Compliance with legal obligations</strong><br />
                Legal basis: Art. 6(1)(c)
              </li>
              <li>
                <strong>Marketing communications (where applicable and permitted)</strong><br />
                Legal basis: Art. 6(1)(a) or Art. 6(1)(f)<br />
                You may opt out at any time.
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">5. Data sharing</h2>
            <p className="text-[#475569] leading-relaxed mb-2">Personal data may be shared with:</p>
            <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-1">
              <li>Hosting, infrastructure, and authentication providers</li>
              <li>Analytics providers (if enabled)</li>
              <li>Legal or professional advisors</li>
              <li>Public authorities where legally required</li>
            </ul>
            <p className="text-[#475569] leading-relaxed mb-6">
              We do not sell personal data.
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">6. International data transfers</h2>
            <p className="text-[#475569] leading-relaxed mb-6">
              Where personal data is transferred outside the EU/EEA, appropriate safeguards are applied, including EU Standard Contractual Clauses (SCCs). Equivalent safeguards apply under Swiss data protection law.
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">7. Data retention</h2>
            <ul className="list-disc pl-6 text-[#475569] mb-6 space-y-1">
              <li>Demo requests: typically retained for 12–24 months after last interaction</li>
              <li>Demo accounts and logs: retained for the duration of access and a limited audit period</li>
              <li>Legal records: retained as required by law</li>
            </ul>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">8. Security measures</h2>
            <p className="text-[#475569] leading-relaxed mb-6">
              Appropriate technical and organizational measures are implemented, including access control, encryption in transit, and monitoring.
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">9. Data subject rights</h2>
            <p className="text-[#475569] leading-relaxed mb-4">
              You may have the right to access, rectify, delete, restrict processing, object to processing, request data portability, or withdraw consent.
            </p>
            <p className="text-[#475569] leading-relaxed mb-6">
              Requests can be sent to: <a href="mailto:contact@0gholding.ch" className="text-[#8B1B3A] hover:underline">contact@0gholding.ch</a>
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">10. Supervisory authorities</h2>
            <p className="text-[#475569] leading-relaxed mb-2">
              EU/EEA users may lodge a complaint with their local supervisory authority.
            </p>
            <p className="text-[#475569] leading-relaxed mb-6">
              Swiss users may contact the Federal Data Protection and Information Commissioner (FDPIC).
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">11. Cookies and analytics</h2>
            <p className="text-[#475569] leading-relaxed mb-6">
              Cookies may be used to ensure functionality and improve the website. Cookie preferences can be managed via browser settings or consent tools where applicable.
            </p>

            <h2 className="text-xl font-semibold text-[#0F172A] mt-8 mb-4">12. Changes to this policy</h2>
            <p className="text-[#475569] leading-relaxed mb-6">
              This policy may be updated from time to time. The "Last updated" date reflects the latest revision.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
